import { useState, useMemo } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../../../../config/theme";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import {
  useDevicesList,
  useAvailableVehicles,
  useAvailableTrailers,
} from "../../../../../../features/devices/queries";
import {
  useCreateDeviceFlow,
  useUpdateDevice,
  useUpdateDeviceStatus,
  useDeleteDevice,
  type DeviceCreationPayload,
} from "../../../../../../features/devices/mutations";
import { useCompaniesStore } from "../../../../../../store/admin/companiesStore";
import { useAllBrands } from "../../../../../../features/catalogs/queries";
import type {
  Device,
  EditFormType,
  NewDeviceFormType,
  AlertState,
  FilterParams,
} from "../types";
import { escapeHtml } from "../../../../../../utils/escapeHtml";

const INITIAL_FORM: NewDeviceFormType = {
  name: "",
  identifier: "",
  model: "",
  type: "gps",
  status_id: "",
  vehicleMode: "existing",
  vehicle_id: "",
  vehicle_name: "",
  vehicle_plate: "",
  vehicle_type: "car",
  vehicle_brand: "",
  vehicle_model: "",
  vehicle_year: "",
  vehicle_fuelType: "",
  vehicle_status_id: "",
  remolqueMode: "none",
  remolque_id: "",
  remolque_name: "",
  remolque_plate: "",
  remolque_type: "dry_van",
  remolque_brand: "",
  remolque_year: "",
  remolque_status_id: "",
  remolque_condition: "",
  remolque_capacity: "",
  remolque_maximumWeight: "",
  remolque_brakeType: "",
  remolque_suspensionType: "",
  serial: "",
  sim_card: "",
  iccid: "",
  phone_number: "",
  installation_date: "",
  activation_date: "",
  brand_id: "",
  sg_condition: "",
  has_panic_button: null,
};

const ACTIVE_STATUS_ID = "R5tW0uX4yY9cF6dH2aI8";
const INACTIVE_STATUS_ID = "S6uX1vY5zZ0dG7eI3bJ9";

function buildCreationPayload(form: NewDeviceFormType): DeviceCreationPayload {
  const deviceKeys: (keyof NewDeviceFormType)[] = [
    "name", "identifier", "model", "type", "status_id",
    "serial", "sim_card", "iccid", "network",
    "phone_number", "installation_date", "activation_date", "brand_id",
    "sg_condition", "has_panic_button",
  ];

  const device: Record<string, unknown> = {};
  for (const key of deviceKeys) {
    const value = form[key];
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && !value.trim()) continue;
    if (typeof value === "object" && !Array.isArray(value)) {
      const hasValue = Object.values(value).some((v) => v !== null && v !== undefined && v !== "");
      if (hasValue) device[key] = value;
      continue;
    }
    device[key] = value;
  }

  const vehicle: DeviceCreationPayload["vehicle"] = { mode: form.vehicleMode };
  if (form.vehicleMode === "existing") {
    vehicle.id = form.vehicle_id;
  } else if (form.vehicleMode === "new") {
    const vData: Record<string, unknown> = {
      name: form.vehicle_name.trim(),
      plate: form.vehicle_plate.trim(),
      lang: "es",
    };
    if (form.vehicle_type) vData.type = form.vehicle_type;
    if (form.vehicle_brand.trim()) vData.brand = form.vehicle_brand.trim();
    if (form.vehicle_model.trim()) vData.model = form.vehicle_model.trim();
    if (form.vehicle_year.trim()) vData.year = Number(form.vehicle_year);
    if (form.vehicle_fuelType) vData.fuelType = form.vehicle_fuelType;
    if (form.vehicle_status_id) vData.status_id = form.vehicle_status_id;
    vehicle.data = vData;
  }

  const remolque: DeviceCreationPayload["remolque"] = { mode: form.remolqueMode };
  if (form.remolqueMode === "existing") {
    remolque.id = form.remolque_id;
  } else if (form.remolqueMode === "new") {
    const fd = new FormData();
    fd.append("name", form.remolque_name.trim());
    fd.append("plate", form.remolque_plate.trim());
    fd.append("lang", "es");
    const optional: [string, string][] = [
      ["type", form.remolque_type],
      ["brand", form.remolque_brand],
      ["year", form.remolque_year],
      ["status_id", form.remolque_status_id],
      ["condition", form.remolque_condition],
      ["capacity", form.remolque_capacity],
      ["maximumWeight", form.remolque_maximumWeight],
      ["brakeType", form.remolque_brakeType],
      ["suspensionType", form.remolque_suspensionType],
    ];
    for (const [key, val] of optional) {
      if (val?.trim()) fd.append(key, val.trim());
    }
    remolque.data = fd;
  }

  return { device, vehicle, remolque };
}

export function useDeviceForm() {
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddDeviceModalOpen, setIsAddDeviceModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"details" | "edit">("details");
  const [editForm, setEditForm] = useState<Partial<EditFormType>>({});
  const [alert, setAlert] = useState<AlertState>({ show: false, title: "", message: "", type: "info" });
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedView, setSelectedView] = useState<"grid" | "list">("list");
  const itemsPerPage = 6;
  const debouncedSearch = useDebounce(searchQuery);
  const companyId = useCompaniesStore((state) => state.companyId);

  const brandsQuery = useAllBrands();
  const allBrands = brandsQuery.data ?? [];
  const allBrandsLoading = brandsQuery.isLoading;

  const filterParams = useMemo<FilterParams>(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      status: selectedStatus || undefined,
      type: selectedType || undefined,
      search: debouncedSearch || undefined,
    }),
    [currentPage, itemsPerPage, selectedStatus, selectedType, debouncedSearch],
  );

  const devicesQuery = useDevicesList(filterParams);
  const devices = devicesQuery.data?.data ?? [];
  const meta = devicesQuery.data?.meta;
  const loading = devicesQuery.isLoading;

  const availableVehiclesQuery = useAvailableVehicles();
  const availableVehicles = availableVehiclesQuery.data ?? [];
  const availableVehiclesLoading = availableVehiclesQuery.isLoading;

  const availableTrailersQuery = useAvailableTrailers();
  const availableTrailers = availableTrailersQuery.data ?? [];
  const availableTrailersLoading = availableTrailersQuery.isLoading;

  const createFlow = useCreateDeviceFlow();
  const updateMutation = useUpdateDevice();
  const statusMutation = useUpdateDeviceStatus();
  const deleteMutation = useDeleteDevice();
  const isSubmitting = createFlow.isPending || updateMutation.isPending;

  const handleStatusChange = (value: string | null) => { setSelectedStatus(value || ""); setCurrentPage(1); };
  const handleTypeChange = (value: string | null) => { setSelectedType(value || ""); setCurrentPage(1); };
  const handleSearchChange = (value: string) => { setSearchQuery(value); setCurrentPage(1); };

  const handleEditClick = (device: Device) => {
    setSelectedDevice(device);
    setEditForm({
      name: device.name || "",
      model: device.model || "",
      status_id: device.status_id || device.status?.id || "",
      fuelLevel: device.fuelLevel || 0,
      batteryLevel: device.batteryLevel || 0,
      mileage: device.mileage || 0,
      hoursMotor: device.hoursMotor || 0,
      type: device.type || "",
      lastLatitude: device.lastLatitude || 0,
      lastLongitude: device.lastLongitude || 0,
      vehicle_id: device.vehicle_id || "",
      identifier: device.identifier || "",
      serial: device.serial || "",
      sim_card: device.sim_card || "",
      iccid: device.iccid || "",
      network: device.network || { ip_address: "", port: "", protocol: "TCP" },
      phone_number: device.phone_number || "",
      installation_date: device.installation_date || "",
      activation_date: device.activation_date || "",
      brand_id: device.brand_id || "",
      sg_condition: (device as { sg_condition?: string }).sg_condition || "",
      fuel_sensor: device.fuel_sensor ?? undefined,
      has_panic_button: device.has_panic_button ?? null,
    });
    setModalType("edit");
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleDetailsClick = (device: Device) => {
    setSelectedDevice(device);
    setModalType("details");
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleInputChange = (field: string, value: unknown) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveChanges = async (formData: Partial<EditFormType>) => {
    if (!selectedDevice) return;

    const payload: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(formData)) {
      if (key === "vehicle_id") { payload[key] = value || null; continue; }
      if (key === "sg_condition") { payload[key] = value || null; continue; }
      // null = volver a «sin revisar»; no se descarta como vacío.
      if (key === "has_panic_button") { payload[key] = value ?? null; continue; }
      // null quita la calibración del sensor de combustible; undefined no la toca.
      if (key === "fuel_sensor") { if (value !== undefined) payload[key] = value; continue; }
      if (value === null || value === undefined || value === "") continue;
      if (typeof value === "object" && !Array.isArray(value)) {
        const hasVal = Object.values(value).some((v) => v !== null && v !== undefined && v !== "");
        if (hasVal) payload[key] = value;
        continue;
      }
      payload[key] = value;
    }

    updateMutation.mutate(
      { id: selectedDevice.id, data: payload as any },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          setSelectedDevice(null);
          setEditForm({});
          setAlert({ show: true, title: "Éxito", message: "Dispositivo actualizado exitosamente", type: "success" });
        },
        onError: (err: any) => {
          setAlert({ show: true, title: "Error", message: err?.message || "Error al actualizar", type: "error" });
        },
      },
    );
  };

  const handleAddDevice = async (formData: NewDeviceFormType) => {
    const isDumax = import.meta.env.VITE_THEME === "dumax";

    if (isDumax && formData.vehicleMode === "none") {
      setAlert({ show: true, title: "Error", message: "Debes seleccionar o crear un vehículo", type: "error" });
      return;
    }
    if (formData.remolqueMode !== "none" && formData.vehicleMode === "none") {
      setAlert({ show: true, title: "Error", message: "Para asociar un remolque primero debes asignar un vehículo", type: "error" });
      return;
    }

    createFlow.mutate(buildCreationPayload(formData), {
      onSuccess: () => {
        setIsAddDeviceModalOpen(false);
        Swal.fire({
          title: "¡Dispositivo creado!",
          text: "El dispositivo se ha creado exitosamente",
          icon: "success",
          confirmButtonText: "Aceptar",
          confirmButtonColor: brand.primary,
        });
      },
      onError: (err: any) => {
        const message = err?.message || "Error al crear el dispositivo";
        setAlert({ show: true, title: "Error", message, type: "error" });
      },
    });
  };

  const changeDeviceStatus = async (device: Device, newStatusId: string, activate: boolean) => {
    const actionLabel = activate ? "activar" : "desactivar";
    const deviceLabel = device.name || device.identifier || device.id;

    const result = await Swal.fire({
      title: `${activate ? "Activar" : "Desactivar"} dispositivo`,
      text: `¿Está seguro de que desea ${actionLabel} el dispositivo "${deviceLabel}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: activate ? brand.primary : "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: `Sí, ${actionLabel}`,
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    statusMutation.mutate(
      { id: device.id, statusId: newStatusId },
      {
        onSuccess: () => {
          Swal.fire({
            title: `¡Dispositivo ${activate ? "activado" : "desactivado"}!`,
            text: `"${deviceLabel}" ha sido ${activate ? "activado" : "desactivado"} correctamente`,
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: brand.primary,
          });
        },
        onError: () => {
          Swal.fire({
            title: "Error",
            text: `No se pudo ${actionLabel} el dispositivo`,
            icon: "error",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#ef4444",
          });
        },
      },
    );
  };

  const handleActivateDevice = (device: Device) => changeDeviceStatus(device, ACTIVE_STATUS_ID, true);
  const handleDeactivateDevice = (device: Device) => changeDeviceStatus(device, INACTIVE_STATUS_ID, false);

  const handleDeleteDevice = async (device: Device) => {
    const deviceLabel = device.name || device.identifier || device.id;
    const vehicleName = device.vehicle?.name;
    const hasVehicle = Boolean(device.vehicle_id || vehicleName);
    const hasRemolque = Boolean(device.remolque);

    const cascadeItems: string[] = [];
    if (hasVehicle) {
      cascadeItems.push(
        `el vehículo asociado${vehicleName ? ` <b>"${vehicleName}"</b>` : ""}`,
      );
    }
    if (hasRemolque) {
      cascadeItems.push("el remolque asociado");
    }

    const cascadeWarning =
      cascadeItems.length > 0
        ? `<br/><br/>En cascada también se eliminarán ${cascadeItems.join(
            " y ",
          )}.`
        : "";

    const result = await Swal.fire({
      title: "Eliminar dispositivo",
      html: `¿Seguro que deseas eliminar el dispositivo <b>"${escapeHtml(deviceLabel)}"</b>?${cascadeWarning}<br/><br/>Se moverá a la <b>Papelera</b> y podrás restaurarlo desde ahí; <b>no</b> es un borrado definitivo.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    deleteMutation.mutate(
      { id: device.id, cascade: true },
      {
        onSuccess: () => {
          Swal.fire({
            title: "¡Dispositivo eliminado!",
            html: `"${escapeHtml(deviceLabel)}" ${
              cascadeItems.length > 0
                ? "y sus conexiones fueron eliminados"
                : "fue eliminado"
            } correctamente`,
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: brand.primary,
          });
        },
        onError: (err: any) => {
          Swal.fire({
            title: "Error",
            text: err?.message || "No se pudo eliminar el dispositivo",
            icon: "error",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#ef4444",
          });
        },
      },
    );
  };

  const closeModal = () => { setIsModalOpen(false); setSelectedDevice(null); document.body.style.overflow = ""; };
  const closeAddModal = () => { setIsAddDeviceModalOpen(false); document.body.style.overflow = ""; };
  const openAddModal = () => { setIsAddDeviceModalOpen(true); document.body.style.overflow = "hidden"; };
  const closeAlert = () => setAlert((prev) => ({ ...prev, show: false }));

  return {
    devices,
    allBrands,
    allBrandsLoading,
    meta,
    loading,
    availableVehicles,
    availableVehiclesLoading,
    availableTrailers,
    availableTrailersLoading,
    companyId,
    selectedDevice,
    editForm,
    newDeviceForm: INITIAL_FORM,
    alert,
    isSubmitting,
    loadingAction: statusMutation.isPending || deleteMutation.isPending,
    isModalOpen,
    isAddDeviceModalOpen,
    modalType,
    selectedStatus,
    selectedType,
    searchQuery,
    currentPage,
    selectedView,
    itemsPerPage,
    handleStatusChange,
    handleTypeChange,
    handleSearchChange,
    handleEditClick,
    handleDetailsClick,
    handleInputChange,
    handleNewDeviceInputChange: () => {},
    handleSaveChanges,
    handleAddDevice,
    handleActivateDevice,
    handleDeactivateDevice,
    handleDeleteDevice,
    setCurrentPage,
    setSelectedView,
    closeModal,
    closeAddModal,
    openAddModal,
    closeAlert,
  };
}
